import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Scale, ExternalLink, CheckCircle2, Loader2 } from "lucide-react";

/**
 * LegalConsentModal
 * Dispara após login para usuários não-super_admin. Checa /auth/legal/pending
 * e exibe modal bloqueante exigindo aceite explícito dos documentos pendentes.
 * O aceite grava timestamp + IP + user_agent no DB (coleção legal_acceptances).
 */
export default function LegalConsentModal() {
  const { user, loading } = useAuth();
  const [pending, setPending] = useState(null); // null = ainda não checado
  const [checked, setChecked] = useState({});    // { document: bool }
  const [busy, setBusy] = useState(false);

  const refetch = async () => {
    try {
      const r = await api.get("/auth/legal/pending");
      setPending(r.data?.pending || []);
      // Pré-marca todos como false (user precisa ticar manualmente)
      const init = {};
      (r.data?.pending || []).forEach((d) => { init[d.document] = false; });
      setChecked(init);
    } catch {
      setPending([]);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { setPending(null); return; }
    if (user.role === "super_admin") { setPending([]); return; }
    // Em sessão impersonada, não exige aceite (super admin está operando como admin_empresa)
    if (user._impersonated_by) { setPending([]); return; }
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loading]);

  if (!user || !pending || pending.length === 0) return null;

  const allChecked = pending.every((d) => checked[d.document]);

  const aceitar = async () => {
    if (!allChecked) {
      toast.error("Marque todas as caixas para aceitar os documentos");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/legal/accept", {
        documents: pending.map((d) => ({ document: d.document, version: d.version })),
      });
      toast.success("Aceite registrado — bem-vindo(a)");
      setPending([]);  // Fecha o modal
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao registrar aceite");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/80 z-[80] flex items-center justify-center p-4 backdrop-blur-sm"
      data-testid="legal-consent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-consent-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-7 space-y-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
            <Scale size={22} />
          </div>
          <div>
            <h2 id="legal-consent-title" className="text-xl font-bold text-slate-900">
              Antes de continuar — Aceite dos documentos legais
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Atualizamos nossos documentos legais. Para seguir usando o DigiPonto, precisamos
              do seu aceite explícito. O registro inclui <strong>data, hora e IP</strong> para
              fins de conformidade LGPD.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {pending.map((d) => (
            <label
              key={d.document}
              data-testid={`legal-doc-${d.document}`}
              className={`flex items-start gap-3 border rounded-xl p-4 cursor-pointer transition
                ${checked[d.document] ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-slate-50"}`}
            >
              <input
                type="checkbox"
                checked={!!checked[d.document]}
                onChange={(e) => setChecked({ ...checked, [d.document]: e.target.checked })}
                data-testid={`check-${d.document}`}
                className="h-5 w-5 mt-0.5 accent-emerald-600 cursor-pointer shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">
                    Li e aceito a <strong>{d.title}</strong>
                  </span>
                  <span className="text-[11px] font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                    v{d.version}
                  </span>
                </div>
                <a
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`link-${d.document}`}
                  className="text-xs text-emerald-700 hover:text-emerald-800 hover:underline inline-flex items-center gap-1 mt-1"
                >
                  Ler o documento completo <ExternalLink size={11} />
                </a>
              </div>
              {checked[d.document] && (
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
              )}
            </label>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
          Ao clicar em <strong>"Aceito e continuar"</strong> você confirma a leitura e concorda
          com os documentos marcados. Esse aceite ficará registrado em nosso sistema de auditoria.
          Caso não concorde, você pode <button
            type="button"
            onClick={() => { localStorage.clear(); window.location.href = "/login"; }}
            data-testid="btn-recusar-sair"
            className="underline font-semibold hover:text-amber-700">sair da conta</button>.
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
          <button
            type="button"
            onClick={aceitar}
            disabled={busy || !allChecked}
            data-testid="btn-aceitar-legal"
            className="h-11 px-6 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 transition"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            {busy ? "Registrando aceite…" : "Aceito e continuar"}
          </button>
        </div>
      </div>
    </div>
  );
}
